import type { AcpSessionConfigOption } from '@crowdex/agent-contracts';
import type { NativeAgent } from '@/lib/native-desktop';

type AcpSelectConfigOption = Extract<AcpSessionConfigOption, { type: 'select' }>;

/**
 * The list an agent last said, on this device: shown at once (with its names), and what the user
 * picks from when the agent cannot say it now. The model picked is an account preference.
 */
export type CachedAgentModels = { model?: AcpSelectConfigOption };

const STORAGE_KEY = 'crowdex.agent-models.v3';

/** One entry per agent and program: another install (or version) of it lists its own models. */
export function agentModelsKey(agent: Pick<NativeAgent, 'id' | 'command'>): string {
  return `${agent.id}\u0000${agent.command}`;
}

function readAll(): Record<string, CachedAgentModels> {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed !== null && typeof parsed === 'object'
      ? (parsed as Record<string, CachedAgentModels>)
      : {};
  } catch {
    return {};
  }
}

export function readAgentModels(key: string): CachedAgentModels {
  const entry = readAll()[key] ?? {};
  return entry.model?.type === 'select' && Array.isArray(entry.model.options)
    ? { model: entry.model }
    : {};
}

/** Records the list the agent said. */
export function saveAgentModels(key: string, entry: CachedAgentModels): void {
  try {
    const all = readAll();
    globalThis.localStorage?.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...all, [key]: { ...all[key], ...entry } }),
    );
  } catch {
    // Storage full or unavailable: the next conversation asks the agent again.
  }
}
